export interface Link {
  rel: string;
  href: string;
  method: string[];
}

export interface Organization {
  orgnr: string;
  name: string;
}

export interface OrganizationRepresentation {
  organization: Organization;
  _links: Link[];
}

export interface OrganizationList {
  organizations: OrganizationRepresentation[];
  _links: Link[];
}

export const CERTIFICATE_ISSUERS = [
  "DIGIPOST",
  "BUYPASS",
  "COMMFIDES",
] as const;

export type CertificateIssuer = (typeof CERTIFICATE_ISSUERS)[number];

export interface Certificate {
  id: string;
  orgnr: string;
  revoked: boolean;
  serialNumber: string;
  subjectDn: string;
  createdTime: string;
  displayName: string;
  issuer: CertificateIssuer;
  notAfter: string;
  unregisteredAt: string | null;
}

export interface CertificateRepresentation {
  certificate: Certificate;
  _links: Link[];
}

export interface CertificateList {
  certificates: CertificateRepresentation[];
  _links: Link[];
}

export const AUDIT_EVENT_TYPES = [
  "ORGANIZATION_CREATED",
  "USER_GRANTED_ACCESS",
  "USER_ACCESS_DELETED",
  "CERTIFICATE_CREATED",
  "CERTIFICATE_REVOKED_BY_ISSUER",
  "CERTIFICATE_REVOKED_BY_USER",
  "OAUTH_CLIENT_CREATED",
  "OAUTH_CLIENT_DELETED",
  "OAUTH_CLIENT_EDITED",
] as const;

export type AuditEventType = (typeof AUDIT_EVENT_TYPES)[number];

export interface AuditLogRepresentation {
  timestamp: string;
  actor: string;
  system: string;
  type: AuditEventType;
  details: Record<string, string>;
}

export const REVOCATION_REASONS = [
  "UNSPECIFIED",
  "KEY_COMPROMISE",
  "CA_COMPROMISE",
  "AFFILIATION_CHANGED",
  "SUPERSEDED",
  "CESSATION_OF_OPERATION",
  "CERTIFICATE_HOLD",
  "PRIVILEGE_WITHDRAWN",
  "AA_COMPROMISE",
] as const;

export type RevocationReason = (typeof REVOCATION_REASONS)[number];

const NON_SELECTABLE_REVOCATION_REASONS: RevocationReason[] = [
  "UNSPECIFIED",
  "CA_COMPROMISE",
];

export const SELECTABLE_REVOCATION_REASONS = REVOCATION_REASONS.filter(
  (reason) => !NON_SELECTABLE_REVOCATION_REASONS.includes(reason),
);

export type CertificateRevocationRequest = {
  reason: RevocationReason;
};

export type CertificateUploadRequest = {
  certificate: string;
  displayName: string;
};

export type CertificateCreationRequest = {
  keyStorePass: string;
  displayName: string;
};

export interface CreatedCertificate {
  certificateRepresentation: CertificateRepresentation;
  keystoreP12: string;
}

export type Application = {
  displayName: string;
  oAuthId: string;
  oAuthResourceIdentifier: string;
};

export type AvailableAccount = {
  accountId: string;
  name: string;
};
export type AccountsByApplication = Record<string, string[]>;
export type AccountsWithNameByApplication = Record<string, AvailableAccount[]>;

export interface OAuthClient {
  clientId: string;
  displayName: string;
  scopes: string[];
  orgnr: string;
  certificateIds: string[];
}

export interface OAuthClientRepresentation {
  client: OAuthClient;
  _links: Link[];
}

export interface OAuthClientList {
  clients: OAuthClientRepresentation[];
  _links: Link[];
}

//brukes på henting av data
export interface AvailableAccountsRepresentation {
  accounts: AccountsWithNameByApplication;
}

export interface OAuthClientCreationRequest {
  displayName: string;
  certificateIds: string[];
  scopes: AccountsByApplication;
}

export interface UpdateClientRequest {
  displayName: string;
  certificateIds: string[];
  scopes: AccountsByApplication;
}

export const USER_ROLES = ["ADMIN", "OWNER"] as const;

export type UserRole = (typeof USER_ROLES)[number];

export interface UserOrganizationAccess {
  orgnr: string;
  nin: string;
  role: UserRole;
}

export interface NewUserRequest {
  nin: string;
  role: UserRole;
}

export interface UserToDelete {
  nin: string;
}
